'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import type { ProductImage } from '@cobrastyle/shared-types';
import { useTranslations } from 'next-intl';
import { cn } from '@cobrastyle/ui';
import { ZoomableImage } from './zoomable-image';

interface ImageGalleryProps {
  images: ProductImage[];
  productName: string;
}

export function ImageGallery({ images, productName }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const t = useTranslations('product');

  // Reset to the first image whenever the image set changes, so picking a
  // different variant shows that variant's own first photo rather than
  // whatever position the customer had reached. Hoisted out of the dependency
  // array so the linter can check it.
  const firstImageUrl = images[0]?.url;
  useEffect(() => {
    setActiveIndex(0);
  }, [firstImageUrl]);

  // Clamped for RENDER, because the effect above runs after it. A variant with
  // four images and one with two are both normal now that a variant's gallery
  // leads with its own photos (see lib/variant-images.ts) — and switching from
  // the fourth image of one to a set of two left this reading images[3] on the
  // render before the reset landed, which threw rather than degrading. The
  // effect still owns the reset; this only keeps the render in bounds.
  const safeIndex = activeIndex < images.length ? activeIndex : 0;

  return (
    <div className="flex flex-col gap-3">
      {/* A recessed panel with the photo contained inside it, not cropped to
          fill it: these are catalogue shots of long objects — a rifle cropped
          square loses the barrel, which is most of the product. */}
      <div className="relative isolate h-[300px] rounded border border-border bg-surface-100 p-6 sm:h-[380px] lg:h-[440px]">
        {images.length > 0 ? (
          <ZoomableImage
            image={images[safeIndex]}
            productName={productName}
            priority
          />
        ) : (
          // Plenty of the catalogue has an empty media_gallery — spare parts
          // especially. The previous fallback pointed at /placeholder.jpg,
          // which is not in public/, so those products drew a broken-image
          // icon and their own name as alt text.
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-sm text-muted">{t('noImage')}</span>
          </div>
        )}
      </div>

      {images.length > 1 && (
        // One scrolling row rather than a wrapping grid. A configurable product
        // now draws its variant's images ahead of the master's (see
        // lib/variant-images.ts), so eight or more thumbnails is normal — and
        // as a grid those wrapped into a second and third row that pushed the
        // description down the page.
        //
        // Fixed-width tiles because `fill` needs a sized parent, and `shrink-0`
        // so they keep that width instead of compressing to fit. Below five
        // images this looks exactly like the grid it replaces.
        //
        // The scrollbar is styled locally: the global one is a solid dark 8px
        // thumb, which under a row of small tiles reads as a second border.
        <div
          className="flex gap-3 overflow-x-auto pb-1 [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5"
        >
          {images.map((image, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`${productName} – ${index + 1}`}
              aria-current={safeIndex === index}
              className={cn(
                'relative isolate h-[76px] w-[116px] shrink-0 snap-start rounded-[3px] border bg-surface-100 transition-colors',
                safeIndex === index
                  ? 'border-accent-600'
                  : 'border-border hover:border-border-hover',
              )}
            >
              <Image
                src={image.url}
                alt=""
                fill
                className="object-contain p-2 mix-blend-multiply"
                sizes="120px"
              />
            </button>
          ))}
        </div>
      )}

    </div>
  );
}
